import { HeapAnalytics, Configuration, Orders } from '@/services'
import { FREE_SHIPPING_AMOUNT } from '@/data/constants'

const { CHECKOUT_EXPERIMENT_ID } = Configuration

export const kebabCase = str => (
  `${str || ''}`.replace(/([a-z])([A-Z])/g, '$1-$2')    // get all lowercase letters that are near to uppercase ones
  .replace(/[\s_]+/g, '-')                // replace all spaces and low dash
  .toLowerCase()
)

export const productClickGa = ({ product, position, category }) => {
  if ('undefined' !== typeof window) {
    const customer = getCustomerDetails()
    const customerId = customer?.customerId
    const customerEmail = customer?.customerEmail
    const visitorType = customerEmail ? "Logged In" : "Guest"
    window.dataLayer = window.dataLayer || []
    window['dataLayer'].push({ ecommerce: null }) // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: 'productClick',
      ecommerce: {
        visitorType,
        customerId,
        click: {
          actionField: { list: kebabCase(category) },
          products: [{
            name: product.name,
            id: product.variants[0].sku,
            price: product.calculated_price,
            brand: product.brand,
            category: category,
            variant: product.variants[0].id,
            position,
          }],
        },
      },
      customerEmail
    })
  }
}

export const productUpdateGA = async (lineItem, event, cartTotal) => {
  try {
    const customer = getCustomerDetails()
    const customerId = customer?.customerId
    const customerEmail = customer?.customerEmail
    const visitorType = customerEmail ? "Logged In" : "Guest"
    const keyName = event === 'addToCart' ? 'add' : 'remove'
    window.dataLayer = window.dataLayer || []
    window['dataLayer'].push({ ecommerce: null }) // Clear the previous ecommerce object.
    window.dataLayer.push({
      event,
      ecommerce: {
        visitorType,
        customerId,
        [keyName]: {
          products: [{
            name: lineItem.name,
            id: lineItem.sku, // for grouped products like quilts, do you have a default that you can use?
            price: lineItem.actualPrice, // for grouped products like quilts, do you have a default that you can use?
            salePrice: lineItem.salePrice, // for grouped products like quilts, do you have a default that you can use?
            brand: lineItem.brand,
            variant: lineItem.variantId, // "Small" | "Large" | etc
            quantity: lineItem.quantity, // passing quantity from product update
          }],
        },
        customerEmail,
      },
      cartTotal,
    })
  } catch (error) {
    console.error(error)
  }
}


export const getCustomerDetails = () => {
  const customerDetails = localStorage.getItem('customer')
  const customer = customerDetails ? JSON.parse(customerDetails) : {}
  return customer
}

export const customerGA = async (cartAmount, pageType) => {
  const customer = getCustomerDetails()
  const customerId = customer?.customerId
  const customerEmail = customer?.customerEmail
  const orderCount = await getCustomerOrderCount(customerId, customerEmail)
  try {
    window.dataLayer.push({
      event: 'customer',
      visitorType: customerId ? 'Logged In' : 'Guest', // Guest OR Logged In
      customerId, // customer ID
      customerEmail, // Hashed customer email
      customerOrdersCount: orderCount, // Lifetime orders for customer
      customerTotalSpent: '', // Lifetime customer value for customer
      cartTotal: cartAmount, // should be the subtotal of the shopping cart
      pageType, // should be one of the following: home|category|product|cart|searchresults|checkout|purchase|other|account
    })
  } catch {
  }
}

export const getCustomerOrderCount = async (customerId, customerEmail) => {
  let orderCount = sessionStorage.getItem('customerOrderCount') || 0
  if (!orderCount && customerId && customerEmail) {
    orderCount = (await Orders.getCustomerOrders({ customerId, customerEmail, sort: 'date_created:desc' })).length
    sessionStorage.setItem('customerOrderCount', orderCount)
  }
  return orderCount
}

export const customerHeap = async (cartAmount, pageType) => {
  const customer = getCustomerDetails()
  const customerId = customer?.customerId
  const customerEmail = customer?.customerEmail

  const orderCount = await getCustomerOrderCount(customerId, customerEmail)
  HeapAnalytics.identify({
    visitorType: customerId ? 'Logged In' : 'Guest',
    customerId,
    customerEmail,
    customerOrdersCount: orderCount,
    pageType,
  })
}

export const getCategoryName = (categories) => [...categories].sort((a, b) => {
  const pathDiff = b.path.split('/').length - a.path.split('/').length
  return pathDiff || (b.name.length - a.name.length)
})[0]?.name

export const formatPrice = (price, digit = 2) => {
  // using parseFloat to make sure this function works for both number and string
  const floatPrice = parseFloat(price)
  if (!isFinite(floatPrice)) {
    return price
  }
  return `$${parseFloat(price).toFixed(digit)}`
}

export const formatPriceRange = (minPrice, maxPrice, digit = 2) =>
  // using parseFloat to make sure this function works for both number and string
  `${formatPrice(minPrice, digit)} - ${formatPrice(maxPrice, digit)}`

export const parsePrice = (price) => {
  if (typeof price === 'number') {
    return price
  }
  return Number.parseFloat(price.startsWith('$') ? price.substring(1) : price)
}

export const getPriceOrPriceRange = (product, priceType) => {
  const { variants } = product
  const sortedVariantPrices = variants?.map((variant) => parseFloat(variant[priceType]))?.sort()
  const minPrice = variants ? sortedVariantPrices[0] : product[priceType]
  const maxPrice = variants ? sortedVariantPrices.pop() : product[priceType]

  return minPrice === maxPrice
    ? formatPrice(minPrice)
    : formatPriceRange(minPrice, maxPrice)
}

export const sizeArray = ['small', 'medium', 'large', 'extra small', 'extra large', 'long', 'tall', 'short']

export const checkVariantNameIsSize = (el) => {
  const name = el.option_values[0].label.toLowerCase()
  if (/^\d/.test(name) || sizeArray.includes(name)) {
    return 1
  }
  return 0
}

export const salePrice = (price, calculatedPrice) => {
  let salePrice = ''
  if (price !== calculatedPrice) {
    salePrice = price
  }
  return salePrice
}


export const isFurniture = (additional_info) => additional_info && additional_info.is_furniture && parseInt(additional_info.is_furniture[0], 10)

export const priceValue = (price) => price.split('$')[1]
export const isProductFreeShipping = (price) => {
  let freeShipping = 0
  price = `${price}`
  if (price.includes('-')) {
    freeShipping = price.split('-').some((value) => priceValue(value) >= FREE_SHIPPING_AMOUNT)
  } else {
    freeShipping = priceValue(price) >= FREE_SHIPPING_AMOUNT
  }
  return freeShipping
}

export const removeSpecialChars = (string, regex = /[^a-zA-Z \s0-9,'\-#.*&]/g) => `${string}`.replace(regex, '')

export const getCookieByName = (name) => {
  const cookieName = globalThis?.document?.cookie?.split(';')?.find((cookie) => cookie.includes(name))
  return cookieName?.split('=')?.[1]
}

// IDs for each variant within an experiment are numeric and indexed at 0
export const DEFAULT_CHECKOUT_VARIANT = '0'

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

/*
  Since google optimize experiement results are not always present on load,
  we need a method to poll for the data OR resort to the default after 500ms.
*/
export const getExperiment = async ({ experimentId, darkMode = false }) => new Promise(async (resolve) => {
  // if darkMode is enabled, by cookie etc., use Variant1
  if (darkMode) {
    resolve(1)
  }
  let iteration = 0
  let variant
  while (!variant && iteration < 10) {
    iteration++
    variant = window?.google_optimize?.get(experimentId)
    if (variant) {
      resolve(variant)
    }
    await sleep(50 + 50 * iteration)
  }
  resolve(DEFAULT_CHECKOUT_VARIANT)
})
